import { Injectable } from '@angular/core';
import { BehaviorSubject, map, Observable } from 'rxjs';

import { ApiService } from '../api.service';
import {
  Activity,
  CreateCultureMediaResponse,
  CreateCultureRequest,
  CreateCultureResponse,
  CreateCultureTabRequest,
  CreateCultureTabResponse,
  Culture,
  News,
  Suggestion,
  SuggestionHistory,
  TeamChanges,
  CommentsChanges
} from '../../interfaces/news.interface';

@Injectable({
  providedIn: 'root',
})
export class NewsService {
  constructor(private apiService: ApiService) {}

  // Buzón

  getSuggestions(): Observable<Suggestion[]> {
    return this.apiService.buildAPIGet<Suggestion[]>('/suggestions/');
  }

  getSuggestionId(id: number): Observable<Suggestion> {
    return this.apiService.buildAPIGetById<Suggestion>(
      '/suggestions/',
      id + '/'
    );
  }

  private pendingSuggestionsSubject = new BehaviorSubject<number>(0);
  pendingSuggestions$ = this.pendingSuggestionsSubject.asObservable();

  refreshPendingSuggestions(): void {
    this.getSuggestions().subscribe({
      next: (suggestions) => {
        const count = suggestions.filter(
          s => s.status === 'Iniciada'
        ).length;

        this.pendingSuggestionsSubject.next(count);
      },
      error: (err) => console.error(err)
    });
  }

  getSuggestionHistory(id: number): Observable<SuggestionHistory[]> {
    return this.apiService.buildAPIGet<SuggestionHistory[]>(
      `/suggestions/${id}/history/`
    );
  }

  updateSuggestion(id: number, data: any): Observable<Suggestion> {
    return this.apiService.buildAPIPut<Suggestion>('/suggestions/', id, data);
  }

  postSuggestion(data: any): Observable<Suggestion> {
    return this.apiService.buildAPIPost<Suggestion>('/suggestions/', data);
  }

  // Actividades

  getActivities(): Observable<Activity[]> {
    return this.apiService.buildAPIGet<Activity[]>('/news/events/');
  }

  getActivitiesId(id: number): Observable<Activity> {
    return this.apiService.buildAPIGetById<Activity>('/news/events/', id + '/');
  }

  postActivities(formData: FormData): Observable<Activity> {
    // Get and properly format the event_days data if it exists
    const eventDaysKey = 'event_days';
    if (
      formData.get(eventDaysKey) &&
      typeof formData.get(eventDaysKey) === 'string'
    ) {
      try {
        // Parse the JSON string
        const eventDaysJson = formData.get(eventDaysKey) as string;
        const eventDays = JSON.parse(eventDaysJson);

        // Remove the original JSON string entry
        formData.delete(eventDaysKey);

        // Add each event day as separate entries
        eventDays.forEach((day: any, index: number) => {
          Object.entries(day).forEach(([key, value]) => {
            formData.append(
              `${eventDaysKey}[${index}][${key}]`,
              value as string
            );
          });
        });
      } catch (e) {
        console.error('Error parsing event_days:', e);
      }
    }

    // Do NOT set content-type header - browser will set it correctly with boundary info for multipart/form-data
    return this.apiService.buildAPIPost<Activity>('/news/events/', formData);
  }

  putActivities(id: number, data: any): Observable<Activity> {
    // If data is FormData, log it and pass it directly
    if (data instanceof FormData) {
      // console.log('FormData being sent to putActivities:');
      // data.forEach((value, key) => {
      //   console.log(`${key}: ${value instanceof File ? value.name : value}`);
      // });

      return this.apiService.buildAPIPut<Activity>('/news/events/', id, data);
    }

    // For backward compatibility - create a new FormData from object data
    // console.log('Converting object to FormData in putActivities');
    const formData = new FormData();

    // Add all properties to the FormData
    Object.keys(data).forEach((key) => {
      if (data[key] !== null && data[key] !== undefined) {
        // Handle special case for event_days which needs JSON formatting
        if (key === 'event_days') {
          formData.append(key, JSON.stringify(data[key]));
        }
        // Handle boolean values specially
        else if (typeof data[key] === 'boolean') {
          formData.append(key, data[key] ? 'true' : 'false');
        }
        // Handle File objects for image uploads
        else if (data[key] instanceof File) {
          formData.append(key, data[key]);
        }
        // Handle other values normally
        else {
          formData.append(key, data[key]);
        }
      }
    });

    // Log the created FormData
    // console.log('Created FormData in putActivities:');
    // formData.forEach((value, key) => {
    //   console.log(`${key}: ${value instanceof File ? value.name : value}`);
    // });

    return this.apiService.buildAPIPut<Activity>('/news/events/', id, formData);
  }

  // Cultura empresarial

  getCulture(): Observable<Culture[]> {
    return this.apiService.buildAPIGet<Culture[]>('/stories/');
  }

  getCultureId(id: number): Observable<Culture> {
    return this.apiService.buildAPIGetById<Culture>('/stories/', id + '/');
  }

  postCulture(data: CreateCultureRequest): Observable<CreateCultureResponse> {
    return this.apiService.buildAPIPost<CreateCultureResponse>(
      '/stories/',
      data
    );
  }

  postCultureTab(
    data: CreateCultureTabRequest,
    id: number
  ): Observable<CreateCultureTabResponse> {
    return this.apiService.buildAPIPost<CreateCultureTabResponse>(
      `/stories/${id}/tabs/`,
      data
    );
  }

  postCultureTabMedia(
    id: number,
    storyId: number,
    file: FormData
  ): Observable<CreateCultureMediaResponse> {
    return this.apiService.buildAPIPost<CreateCultureMediaResponse>(
      `/stories/${storyId}/tabs/${id}/media/`,
      file
    );
  }

  deleteCulture(id: number): Observable<void> {
    return this.apiService.buildAPIDelete<void>('/stories/', id);
  }

  updateCulture(
    id: number,
    data: CreateCultureRequest
  ): Observable<CreateCultureResponse> {
    return this.apiService.buildAPIPut<CreateCultureResponse>(
      '/stories/',
      id,
      data
    );
  }

  updateCultureTab(
    storyId: number,
    tabId: number,
    data: CreateCultureTabRequest
  ): Observable<CreateCultureTabResponse> {
    return this.apiService.buildAPIPut<CreateCultureTabResponse>(
      `/stories/${storyId}/tabs/`,
      tabId,
      data
    );
  }

  deleteCultureTab(storyId: number, tabId: number): Observable<void> {
    return this.apiService.buildAPIDelete<void>(
      `/stories/${storyId}/tabs/`,
      tabId
    );
  }

  // Noticias
  getNews(): Observable<News[]> {
    return this.apiService.buildAPIGet<News[]>('/news/news/');
  }

  getNewsId(id: number): Observable<News> {
    return this.apiService.buildAPIGetById<News>('/news/news/', id + '/');
  }

  postNews(data: any): Observable<News> {
    // If data is already FormData, use it directly
    if (data instanceof FormData) {
      return this.apiService.buildAPIPost<News>('/news/news/', data);
    }

    // Create FormData object for multipart/form-data submission (backward compatibility)
    const formData = new FormData();

    // Explicitly add each field to the FormData
    if (data.title) formData.append('title', data.title);
    if (data.content) formData.append('content', data.content);
    if (data.category) formData.append('category', data.category);
    if (data.notifications !== undefined)
      formData.append('notifications', data.notifications ? 'true' : 'false');
    if (data.publication_date)
      formData.append('publication_date', data.publication_date);
    if (data.tags_string || data.tags_string === '')
      formData.append('tags_string', data.tags_string);
    if (data.featured !== undefined)
      formData.append('featured', data.featured ? 'true' : 'false');
    if (data.published !== undefined)
      formData.append('published', data.published ? 'true' : 'false');
    if (data.image) formData.append('image', data.image);

    return this.apiService.buildAPIPost<News>('/news/news/', formData);
  }

  putNews(id: number, data: any): Observable<News> {
    // If data is already FormData, it should already have the correct field mappings
    if (data instanceof FormData) {
      // For debugging
      // console.log('FormData being sent to putNews:');
      data.forEach((value, key) => {
        // console.log(`${key}: ${value instanceof File ? value.name : value}`);
      });

      return this.apiService.buildAPIPut<News>('/news/news/', id, data);
    }

    // Otherwise, create a new FormData object with explicit handling for all fields
    const formData = new FormData();

    // Add standard text fields
    if (data.title) formData.append('title', data.title);
    if (data.content) formData.append('content', data.content);
    if (data.category) formData.append('category', data.category);
    if (data.publication_date)
      formData.append('publication_date', data.publication_date);
    if (data.tags_string || data.tags_string === '')
      formData.append('tags_string', data.tags_string);

    // Handle boolean fields explicitly
    formData.append('published', data.published ? 'true' : 'false');
    formData.append('featured', data.featured ? 'true' : 'false');
    if (data.notifications !== undefined)
      formData.append('notifications', data.notifications ? 'true' : 'false');

    // Special handling for image
    if (data.image instanceof File) {
      formData.append('image', data.image);
    }

    // Add specific flag for image removal
    if (data.remove_image === 'true' || data.remove_image === true) {
      formData.append('remove_image', 'true');
    }

    // For debugging
    //console.log('FormData created in putNews:');
    formData.forEach((value, key) => {
      //console.log(`${key}: ${value instanceof File ? value.name : value}`);
    });

    // Fixed: Use correct endpoint '/news/news/' instead of '/news/'
    return this.apiService.buildAPIPut<News>('/news/news/', id, formData);
  }

  deleteMailbox(id: number): Observable<void> {
    return this.apiService.buildAPIDelete<void>('/suggestions/', id);
  }

  // Team Changes (New endpoint)
  getTeamChanges(): Observable<TeamChanges[]> {
    return this.apiService.buildAPIGet<TeamChanges[]>('/news/team-changes/');
  }

  // Comments(all) (New endpoint)
  getComments(): Observable<CommentsChanges[]> {
    return this.apiService.buildAPIGet<CommentsChanges[]>('/reactions/getComments/');
  }

  setModerationComment(id: number, value: boolean) {
    const data = {
        id: id,
        value: value
      };
      return this.apiService.buildAPIPost('/reactions/setModerationComment/',data);  
  }

  setModerationImageComment(id: number, value: boolean) {
    const data = {
        id: id,
        value: value
      };
      return this.apiService.buildAPIPost('/reactions/setModerationImageComment/',data);  
  }

  setModerationImageEvent(id: number, value: boolean) {
    const data = {
        id: id,
        value: value
      };
      return this.apiService.buildAPIPost('/reactions/setModerationImageEvent/',data);  
  }



  setModerationEventComment(id: number, value: boolean) {
    const data = {
        id: id,
        value: value
      };
      return this.apiService.buildAPIPost('/reactions/setModerationEventsComment/',data);  
  }


  getTeamChangesId(id: number): Observable<TeamChanges> {
    return this.apiService.buildAPIGetById<TeamChanges>(
      '/news/team-changes/',
      id + '/'
    );
  }

  postTeamChanges(data: FormData): Observable<TeamChanges> {
    return this.apiService.buildAPIPost<TeamChanges>(
      '/news/team-changes/',
      data
    );
  }

  putTeamChanges(id: number, data: FormData): Observable<TeamChanges> {
    return this.apiService.buildAPIPut<TeamChanges>(
      '/news/team-changes/',
      id,
      data
    );
  }

  deleteTeamChanges(id: number): Observable<void> {
    return this.apiService.buildAPIDelete<void>('/news/team-changes/', id);
  }

  deleteActivity(id: number): Observable<void> {
    return this.apiService.buildAPIDelete<void>('/news/events/', id);
  }

  deleteNews(id: number): Observable<void> {
    const timestamp = new Date().toISOString();
    // console.log(`[${timestamp}] NewsService.deleteNews called for ID: ${id}`);

    const deleteObservable = this.apiService.buildAPIDelete<void>(
      '/news/news/',
      id
    );

    // console.log(`[${timestamp}] Observable created for deleteNews ID: ${id}`);

    return deleteObservable;
  }
}
